import {
  Banknote,
  CreditCard,
  Landmark,
  Smartphone,
  Truck,
  type LucideIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Chip } from '../ui/Chip';
import { IconBadge } from '../ui/IconBadge';
import type { PayoutMethod } from '../utils/catalog';

interface PayoutStepProps {
  available: PayoutMethod[];
  selected: PayoutMethod | null;
  onSelect: (method: PayoutMethod) => void;
  /**
   * Optional per-method hint override. When a key is present and non-empty
   * the chip's second line uses it verbatim — typically a comma-separated
   * list of real payer display names sourced from the catalog (e.g.
   * `"Banrural, Western Union y más"`). Methods missing from this map fall
   * back to the static `methods.payout.${m}.hint` i18n string.
   */
  hints?: Partial<Record<PayoutMethod, string>>;
}

/**
 * Per-payout-method icon mapping. Same icon system as `FundingStep` (lucide
 * stroke icons that inherit `currentColor` from the `IconBadge`).
 *
 * Mapping rationale — payout is the *recipient's* side of the flow, so we
 * pick icons that lean on what the recipient touches:
 *   - BANK_ACCOUNT  → `Landmark` (their bank). Mirrors the ACH icon on the
 *     funding step — consistent because both map to "a bank account",
 *     just on opposite sides of the transfer.
 *   - CASH_PICKUP   → `Banknote` (physical bills handed over at Banrural /
 *     Western Union / etc.). Deliberately different from the funding step's
 *     `DollarSign` for `CASH_DEPOSIT`: funding `$` is the symbolic
 *     "sender pays cash"; payout banknote is the literal "recipient
 *     receives bills" — a small narrative cue.
 *   - PUSH_TO_CARD  → `CreditCard` (same glyph as `DEBIT_CARD` funding;
 *     the label disambiguates).
 *   - MOBILE_WALLET → `Smartphone` (the wallet lives on the recipient's
 *     phone — Tigo Money, Banrural). Differentiated from the funding-side
 *     `Wallet` icon, which represents a hosted wallet *provider*.
 *   - HOME_DELIVERY → `Truck` (universal "delivery to your door"
 *     pictogram; clearer than a generic home/door icon).
 *
 * Labels and hints come from the `methods.payout.*` i18n bundle.
 */
const PAYOUT_ICON: Record<PayoutMethod, LucideIcon> = {
  BANK_ACCOUNT: Landmark,
  CASH_PICKUP: Banknote,
  PUSH_TO_CARD: CreditCard,
  MOBILE_WALLET: Smartphone,
  HOME_DELIVERY: Truck,
};

export const PayoutStep = ({
  available,
  selected,
  onSelect,
  hints,
}: PayoutStepProps) => {
  const { t } = useTranslation('smart');

  return (
    <div className="flex flex-col gap-2" role="radiogroup">
      {available.map((m) => {
        const Icon = PAYOUT_ICON[m];
        // Prefer the catalog-derived hint when the parent supplied one for
        // this method (and it's non-empty — protects against the catalog
        // returning zero payers on slow networks). Otherwise fall back to
        // the static i18n hint baked into the bundle.
        const dynamicHint = hints?.[m];
        const description = dynamicHint && dynamicHint.length > 0
          ? dynamicHint
          : t(`methods.payout.${m}.hint`);
        return (
          <Chip
            key={m}
            layout="horizontal"
            selected={selected === m}
            onClick={() => onSelect(m)}
            icon={
              <IconBadge
                variant={selected === m ? 'accent' : 'default'}
                size="md"
              >
                {/* Same sizing as FundingStep — 20px lucide icon inside the
                  * 40px (h-10 w-10) `IconBadge` md container, with lucide's
                  * default `strokeWidth={2}` for visual weight parity. */}
                <Icon size={20} strokeWidth={2} aria-hidden />
              </IconBadge>
            }
            label={t(`methods.payout.${m}.label`)}
            description={description}
          />
        );
      })}
    </div>
  );
};
